import { ExclamationTriangleIcon } from '@heroicons/react/16/solid'
import { isBlueberry } from '../lib/pesticides'
import type { PesticideDetail, PesticideLabel, PesticideUsePattern } from '../lib/types'

export type ReadyLabel = Extract<PesticideLabel, { status: 'ready' }>

/** The wizard's application methods (spray.tsx), as labels tend to word them. */
const METHOD_WORDS: { method: string; pattern: RegExp }[] = [
  { method: 'foliar', pattern: /foliar|foliage/i },
  // "Airblast" sprayers are ground equipment, so only aircraft wording counts as air.
  { method: 'air', pattern: /aerial|aircraft|helicopter/i },
  { method: 'ground', pattern: /ground|boom|airblast|air-blast|backpack|hand-held/i },
  { method: 'furrow', pattern: /furrow/i },
  { method: 'seed', pattern: /seed treat/i },
]

const unique = <T,>(xs: T[]) => [...new Set(xs)]

/**
 * What a label says about blueberries, ready for the wizard: the PHI range, the label's rate values
 * and units, and an application method when the label names exactly one. Uses only the blueberry
 * rows, so a figure for another crop can never be offered for a blueberry field.
 */
export function blueberryUse(label: ReadyLabel) {
  const patterns = label.usePatterns.filter(isBlueberry)
  const phis = patterns.flatMap((p) => (p.phiDays === null ? [] : [p.phiDays]))
  const rates = new Map<string, { value: number; unit: string }>()
  for (const p of patterns) {
    if (!p.applicationRateUnit) continue
    for (const n of [p.applicationRateMin, p.applicationRateMax]) {
      if (n !== null && n > 0) rates.set(`${n} ${p.applicationRateUnit}`, { value: n, unit: p.applicationRateUnit })
    }
  }
  const methods = unique(patterns.flatMap((p) => METHOD_WORDS.filter((m) => p.applicationMethod && m.pattern.test(p.applicationMethod)).map((m) => m.method)))
  return {
    patterns,
    phi: phis.length ? { min: Math.min(...phis), max: Math.max(...phis) } : null,
    rates: [...rates.values()],
    method: methods.length === 1 ? methods[0]! : null,
  }
}

/** Fill-in buttons only come from a label a person has checked (PRODUCT.md §9, §13 #14). */
export const canSuggest = (label: ReadyLabel) => label.reviewStatus === 'approved'

const days = (n: number) => `${n} day${n === 1 ? '' : 's'}`
const range = (min: number, max: number, fmt: (n: number) => string) => (min === max ? fmt(min) : `${min}–${fmt(max)}`)

function Fact({ term, children }: { term: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs/5 font-medium text-zinc-500 dark:text-zinc-400">{term}</dt>
      <dd className="text-sm/6 text-zinc-950 dark:text-white">{children}</dd>
    </div>
  )
}

function Box({ tone = 'zinc', children }: { tone?: 'zinc' | 'amber'; children: React.ReactNode }) {
  return (
    <div
      className={
        tone === 'amber'
          ? 'rounded-lg bg-amber-50 p-3 text-sm/6 text-amber-900 dark:bg-amber-500/10 dark:text-amber-200'
          : 'rounded-lg bg-zinc-950/[0.03] p-3 text-sm/6 text-zinc-600 ring-1 ring-zinc-950/5 dark:bg-white/5 dark:text-zinc-400 dark:ring-white/10'
      }
    >
      {children}
    </div>
  )
}

function restrictionsOf(patterns: PesticideUsePattern[]) {
  return unique(patterns.flatMap((p) => (p.restrictionsText ? [p.restrictionsText] : [])))
}

/**
 * What the label says, under a picked product: the blueberry rate, PHI, REI, resistance group and
 * season limit, with cautions folded away and the source underneath. Facts from an unreviewed label
 * are shown as such; when there is no label data, it says to check the jug.
 */
export function LabelFacts({ detail }: { detail: { pesticide: PesticideDetail | null; loading: boolean; error: string | null; pollTimedOut: boolean } }) {
  const { pesticide, loading, error, pollTimedOut } = detail
  if (loading || (pesticide?.label.status === 'processing' && !pollTimedOut)) return <Box>Reading the label…</Box>
  if (error || !pesticide || pesticide.label.status !== 'ready') return <Box>No label details yet. Check the rate, PHI and REI on the product label.</Box>

  const label = pesticide.label
  const use = blueberryUse(label)
  if (use.patterns.length === 0) {
    return (
      <Box tone="amber">
        <span className="font-medium">No blueberry use found on this label.</span> Check that it is registered for blueberries before applying.
      </Box>
    )
  }
  const maxApps = use.patterns.flatMap((p) => (p.maxApplicationsPerSeason === null ? [] : [p.maxApplicationsPerSeason]))
  const restrictions = restrictionsOf(use.patterns)
  const ppe = [label.ppeMixingLoading, label.ppeApplication].filter(Boolean).join(' ')

  return (
    <div className="space-y-2">
      {!canSuggest(label) && (
        <Box tone="amber">
          <span className="font-medium">Not yet checked by a person.</span> Read automatically from the label, so confirm it on the jug.
        </Box>
      )}
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-lg bg-zinc-950/[0.03] p-3 ring-1 ring-zinc-950/5 dark:bg-white/5 dark:ring-white/10">
        <div className="col-span-2">
          <Fact term="Label rate · blueberry">{unique(use.patterns.map((p) => p.applicationRateText)).join('; ')}</Fact>
        </div>
        <Fact term="Pre-harvest (PHI)">{use.phi ? range(use.phi.min, use.phi.max, days) : 'Not stated'}</Fact>
        <Fact term="Re-entry (REI)">{label.reiHours !== null ? `${label.reiHours} hours` : (label.reiText ?? 'Not stated')}</Fact>
        <Fact term="Chemical group">{label.resistanceManagementGroup ?? 'Not stated'}</Fact>
        <Fact term="Max per season">{maxApps.length ? range(Math.min(...maxApps), Math.max(...maxApps), (n) => `${n} applications`) : 'Not stated'}</Fact>
      </dl>
      {(restrictions.length > 0 || ppe || label.signalWord) && (
        <details className="group rounded-lg bg-amber-50 px-3 py-2 text-sm/6 text-amber-900 dark:bg-amber-500/10 dark:text-amber-200">
          <summary className="flex cursor-pointer list-none items-center gap-2 font-medium select-none">
            <ExclamationTriangleIcon className="size-4 shrink-0" />
            Cautions{label.signalWord ? ` · ${label.signalWord}` : ''}
          </summary>
          <div className="mt-2 space-y-2">
            {restrictions.map((r) => (
              <p key={r}>{r}</p>
            ))}
            {ppe && <p>Protective equipment: {ppe}</p>}
          </div>
        </details>
      )}
      <p className="text-xs/5 text-zinc-500 dark:text-zinc-400">
        Source: Health Canada label, fetched {new Date(label.fetchedAt).toLocaleDateString()}.{' '}
        <a href={`/pesticides/${pesticide.id}`} target="_blank" rel="noreferrer" className="font-medium text-emerald-700 underline dark:text-emerald-400">
          Full label details
        </a>
      </p>
    </div>
  )
}
